import {createSelector} from 'reselect';
import {info, icons} from '../utils/errorEvents';
import {getListingPage, getTranslations} from './store';
import {getTravelerSegmentFeatures} from './travelerSegmentFeatures';
import {getIncompleteDates, getDateSuccess, getDatesError} from './tripDates';
import getPriceStatus from './priceStatus';
import {isInquiryModalOpen} from './modalStates';
import {getContactOwnerFormHasTripDetailsErrors} from './contactOwnerForm';
import {getGuestsError} from './tripGuests';
import {getPriceTransparencyTestOn} from './primaryCallToActionButton';

// We are currently showing one error at a time, so this will return the error that takes priority, not all of them.
const getCurrentError = createSelector(
    [getDatesError, getGuestsError, getPriceStatus],
    (datesError, guestsError, priceStatus) => {
        const {priceError} = priceStatus;

        return priceError || datesError || guestsError || null;
    }
);

// We are currently showing one error at a time, so this will return the error that takes priority, not all of them.
const getInquiryModalError = createSelector(
    [getGuestsError],
    (guestsError) => {
        let inquiryError = {};

        if (guestsError) {
            inquiryError = Object.assign({}, guestsError);
            inquiryError.icon = icons.DANGER;
        }

        return inquiryError;
    }
);

// Determines if an alert should be shown
/* eslint-disable complexity */
const getShowAlert = createSelector(
    [
        getCurrentError,
        getIncompleteDates,
        getListingPage,
        getDateSuccess,
        isInquiryModalOpen,
        getTravelerSegmentFeatures
    ],
    (
        currentError,
        incompleteDates,
        listingPage,
        dateSuccess,
        inquiryModalOpen,
        travelerSegmentFeatures
    ) => {
        if (
            ((currentError && currentError.type === 'guests') || !!dateSuccess) &&
            travelerSegmentFeatures.redirectToPinnedSerpOnDateEntry &&
            !inquiryModalOpen
        ) {
            return false;
        }

        const shouldShowOnMainPage =
            (!listingPage.loadingRate && (incompleteDates || !!currentError || !!dateSuccess)) ||
            false;
        return shouldShowOnMainPage;
    }
);
/* eslint-enable complexity */

// Determines if an alert should be shown
const getEnterDatesTimerComplete = createSelector(
    [getListingPage],
    (listingPage) => {
        return listingPage.enterDatesTimerComplete;
    }
);

// Determines if an alert should be shown
const getShowInquiryAlert = createSelector(
    [getContactOwnerFormHasTripDetailsErrors, isInquiryModalOpen, getShowAlert],
    (contactOwnerFormHasTripDetailsErrors, inquiryModalOpen, showAlert) => {
        const shouldShowOnMainPage = showAlert;
        const shouldShowInInquiryModal = contactOwnerFormHasTripDetailsErrors;
        return inquiryModalOpen ? shouldShowInInquiryModal : shouldShowOnMainPage;
    }
);

// Determines what alert should show
const getAlertInfo = createSelector(
    [
        getTranslations,
        getIncompleteDates,
        getDateSuccess,
        getCurrentError,
        getPriceTransparencyTestOn
    ],
    (
        translations,
        incompleteDates,
        dateSuccess,
        currentError,
        isMobilePriceTransparencyEnabled
    ) => {
        let enterDatesForPricing = isMobilePriceTransparencyEnabled
            ? info.DATES_ACCURATE_TEST
            : info.DATES_ACCURATE;
        enterDatesForPricing.label = translations[enterDatesForPricing.translationKey];
        const mainPageAlertInfo = incompleteDates
            ? enterDatesForPricing
            : currentError || dateSuccess;

        return mainPageAlertInfo;
    }
);

// Determines what alert should show
const getInquiryAlertInfo = createSelector(
    [
        getTranslations,
        getIncompleteDates,
        getInquiryModalError,
        getDateSuccess,
        getCurrentError,
        isInquiryModalOpen
    ],
    (
        translations,
        incompleteDates,
        inquiryModalError,
        dateSuccess,
        currentError,
        inquiryModalOpen
    ) => {
        let enterDatesForPricing = info.DATES_ACCURATE;
        enterDatesForPricing.label = translations[enterDatesForPricing.translationKey];

        const mainPageAlertInfo = incompleteDates
            ? enterDatesForPricing
            : currentError || dateSuccess;
        const inquiryModalAlertInfo = inquiryModalOpen ? inquiryModalError : null;

        return inquiryModalAlertInfo || mainPageAlertInfo;
    }
);

const getTripAlertData = createSelector(
    [getShowAlert, getAlertInfo, getIncompleteDates, getPriceTransparencyTestOn],
    (showAlert, alertInfo, incompleteDates, isMobilePriceTransparencyEnabled) => {
        return {
            alertInfo,
            showAlert,
            showEnterDatesTooltip: !isMobilePriceTransparencyEnabled ? incompleteDates : false
        };
    }
);

const getTripAlertDataForInquiry = createSelector(
    [getTripAlertData, getShowInquiryAlert, getInquiryAlertInfo],
    (tripDetails, showInquiryAlert, inquiryAlertInfo) => {
        const inquiryTripDetails = Object.assign({}, tripDetails, {
            showAlert: showInquiryAlert,
            alertInfo: inquiryAlertInfo,
            showEnterDatesTooltip: false
        });

        return inquiryTripDetails;
    }
);

const getTripAlertTooltip = createSelector(
    [getAlertInfo, getEnterDatesTimerComplete],
    (alertInfo, enterDatesTimerComplete) => {
        return {
            alertInfo,
            enterDatesTimerComplete
        };
    }
);

export default getTripAlertData;
export {
    getCurrentError,
    getInquiryModalError,
    getShowAlert,
    getShowInquiryAlert,
    getAlertInfo,
    getInquiryAlertInfo,
    getTripAlertData,
    getTripAlertDataForInquiry,
    getTripAlertTooltip
};
